import { Injectable } from '@angular/core';
import { Observable, of, lastValueFrom, Subject } from 'rxjs';
import { FileManagementService } from '../api/file-management.service';
import { AdminFileType } from '../interfaces/file-management-interfaces';
import { TranslocoService } from '@jsverse/transloco';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../api/api-error.service';


@Injectable({ providedIn: 'root' })
export class FileManagementUploadHelper {
  private toast?: ToastComponent;
  readonly completed$ = new Subject<{ success: number }>();

  constructor(private fileManagement: FileManagementService, private transloco: TranslocoService, private apiError: ApiErrorService) { }

  attachToast(toast?: ToastComponent) {
    this.toast = toast;
  }

  click(fileInput: HTMLInputElement) {
    fileInput.click();
  }

  async onFileSelected(event: Event, type: AdminFileType) {
    const target = event.target as HTMLInputElement;
    const list = target.files;
    if (list?.length) {
      const files = this.filterAllowedFiles(list);
      await this.uploadFilesSequentially(files, type);
      target.value = '';
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  async onDrop(event: DragEvent, type: AdminFileType) {
    event.preventDefault();
    event.stopPropagation();
    const list = event.dataTransfer?.files;
    if (list?.length) {
      const files = this.filterAllowedFiles(list);
      await this.uploadFilesSequentially(files, type);
    }
  }

  private filterAllowedFiles(fileList: FileList | File[]): File[] {
    const exts = ['.xls', '.xlsx'];
    return Array.from(fileList).filter(f =>
      exts.some(ext => f.name.toLowerCase().endsWith(ext))
    );
  }

  private async uploadFilesSequentially(files: File[], type: AdminFileType) {
    let success = 0;

    for (const file of files) {
      try {
        await lastValueFrom(this.pickEndpoint(file, type));
        this.showSuccessToast(file.name);
        success++;
      }
      catch (error) {
        var showError = true;
        let crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

          if (error.error.code === 'SVC-4030' || error.error.code === 'SVC-4031' || error.error.code === 'SVC-5200' || error.error.code === 'SVC-5201') {
            showError = false;
            this.toast?.showToast(this.transloco.translate('shared.error'), this.transloco.translate('shared.toast.invalid_excel_format', { fileName: file.name }), 'error', 5000, false);
          }
          else if (error.error.code === 'SVC-4032') {
            showError = false;
            this.toast?.showToast(this.transloco.translate('shared.error'), this.transloco.translate('shared.toast.invalid_number_of_sheets', { error: error.error.message || '' }), 'error', 8000, false);
          }
        }

        if (showError) {
          this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        continue;
      }
    }

    this.completed$.next({ success });
  }

  private pickEndpoint(file: File, type: AdminFileType): Observable<any> {
    if (type === AdminFileType.TAXES_GAS) {
      return this.fileManagement.uploadGasTaxes(file);
    }
    else if (type === AdminFileType.DISTRIBUTION_TARIFF_GAS) {
      return this.fileManagement.uploadGasDistributionTariff(file);
    }
    else if (type === AdminFileType.ACCISE_GAS) {
      return this.fileManagement.uploadAcciseGas(file);
    }
    else if (type === AdminFileType.PRICE_TABLE) {
      return this.fileManagement.uploadElectricityAndGasPrices(file);
    }
    else if (type === AdminFileType.ATECO_CURVES) {
      return this.fileManagement.uploadAtecoCurves(file);
    }
    else if (type === AdminFileType.CURVE_DATA) {
      return this.fileManagement.uploadCurveData(file);
    }
    else if (type === AdminFileType.TARIFF_TABLE_EE) {
      return this.fileManagement.uploadTariffTableEe(file);
    }
    else if (type === AdminFileType.DISTRIBUTION_TARIFF_EE) {
      return this.fileManagement.uploadDistributionTariffEe(file);
    }
    return of(null);
  }

  private showSuccessToast(fileName: string) {
    if (!this.toast) return;
    const title = this.transloco
      ? this.transloco.translate('shared.file_upload.successful')
      : 'Upload successful';
    const message = this.transloco
      ? this.transloco.translate('shared.file_upload.file_uploaded_and_extracted', { fileName: fileName })
      : `“${fileName}” uploaded`;
    this.toast.showToast(title, message, 'success', 4000, false);
  }
}
